import mockPageData from 'lib/mockData/page-data';
import { setMockPianoConfig } from 'services/Piano';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { mockHeadData } from 'utils/metaDataUtils/headData';
import type { ParsedUrlQuery } from 'querystring';
import type { SitemapLink, SitemapPageType, SitemapPageLayout } from 'interfaces/navigation/Sitemap';
import getFooter from 'api/Footer/getFooter';
import type { HeaderNav } from 'interfaces/navigation/Header';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import { getSitemapPageData } from 'utils/miscUtils/sitemapHelpers';
import { validateAlphabetLetter } from 'utils/stringUtils';
import { SitemapPage } from 'pages/Sitemap/SitemapPage';
import type { Breadcrumb } from 'interfaces/navigation/Breadcrumbs';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import getMenu from 'api/Header/getMenu';
import { safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { sitemapQuickPaths } from 'utils/dateUtils';

interface SitemapNextPageProps {
  urlList: SitemapLink[];
  breadcrumbList: Breadcrumb[];
  sitemapPageLayout: SitemapPageLayout;
}

const SitemapNextPage: NextPage<SitemapNextPageProps> = ({
  urlList,
  breadcrumbList,
  sitemapPageLayout,
}: SitemapNextPageProps) => (
  <SitemapPage
    urlList={urlList}
    breadcrumbList={breadcrumbList}
    sitemapPageLayout={sitemapPageLayout}
    alphabetFilter
  />
);

export default SitemapNextPage;

interface Params extends ParsedUrlQuery {
  param: string;
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { param } = context.params as Params;
    const isParamAlphabetValid = validateAlphabetLetter(param);
    if (!isParamAlphabetValid) {
      throw new NotFoundError('Sitemap Page Not Found');
    }
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const arcSpanPageConfig: ArcSpanPageConfig = setarcSpanPageConfig(mockPageData);
    const pianoPageConfig: PianoPageConfig = setMockPianoConfig();
    let sitemapPageType: SitemapPageType = 'companies';
    if (sitemapQuickPaths.includes(param)) {
      sitemapPageType = param as SitemapPageType;
    }
    const { pageTitle, urlList, canonicalUrl, breadcrumbList, sitemapPageLayout } = await getSitemapPageData(
      sitemapPageType,
      context,
    );
    const headData = { ...mockHeadData, canonicalUrl, title: pageTitle };

    return {
      props: {
        arcSpanPageConfig,
        breadcrumbList,
        footer,
        headData,
        headerNav: menu,
        pageTitle,
        pianoPageConfig,
        sitemapPageLayout,
        urlList,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<Params> = () => {
  if (process.env.TEST || process.env.ENVIRONMENT === 'dev') {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: { param: 'A' },
        },
      ],
    };
  }
  return {
    fallback: 'blocking',
    paths: [],
  };
};
